import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import workerProfileService from '../../services/workerProfileService';
import serviceAreaContentService from '../../services/serviceAreaContentService';

const JobSectorBrowserHVACSystems = () => {
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedSystemType, setSelectedSystemType] = useState('all');
  const [locationRadius, setLocationRadius] = useState('50');
  const [certifiedInstallers, setCertifiedInstallers] = useState([]);
  const [loadingInstallers, setLoadingInstallers] = useState(true);
  const [news, setNews] = useState([]);
  const [areaContent, setAreaContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    loadWorkerProfiles();
    loadNewsAndContent();
  }, []);

  const handleContactClick = (installer) => {
    // Handle contact action - could open modal, navigate, or trigger contact flow
    console.log('Contact installer:', installer);
    // Add your contact logic here
  };

  const loadWorkerProfiles = async () => {
    try {
      setLoadingInstallers(true);
      const data = await workerProfileService?.getWorkerProfilesByServiceArea('HVAC Systemen');
      setCertifiedInstallers(data || []);
    } catch (error) {
      console.error('Error loading worker profiles:', error);
    } finally {
      setLoadingInstallers(false);
    }
  };

  const loadNewsAndContent = async () => {
    try {
      setLoadingContent(true);
      const [newsData, contentData] = await Promise.all([
        serviceAreaContentService?.getNewsByServiceArea('HVAC Systemen'),
        serviceAreaContentService?.getContentByServiceArea('HVAC Systemen')
      ]);
      setNews(newsData || []);
      setAreaContent(contentData || []);
    } catch (error) {
      console.error('Error loading news and content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const hvacJobs = [
    {
      title: 'Residentiële verwarming installatie',
      location: 'Amsterdam',
      budget: '€4.500 - €7.000',
      deadline: '3 weken',
      description: 'Installatie CV-ketel en radiatoren in nieuwbouwwoning',
      systemType: 'Verwarming',
      equipment: 'HR-ketel, radiatoren',
      capacity: '24 kW',
      certification: 'F-gas, VCA',
      hourlyRate: '€70',
      energyRating: 'A+++'
    },
    {
      title: 'Commerciële koeling kantoorpand',
      location: 'Rotterdam',
      budget: '€25.000 - €40.000',
      deadline: '2 maanden',
      description: 'VRV-systeem voor klimaatbeheersing 800m² kantoorruimte',
      systemType: 'Koeling',
      equipment: 'VRV-systeem, fancoils',
      capacity: '120 kW',
      certification: 'F-gas, EPA',
      hourlyRate: '€85',
      energyRating: 'A++'
    },
    {
      title: 'Industriële ventilatie fabriek',
      location: 'Eindhoven',
      budget: '€50.000 - €75.000',
      deadline: '3 maanden',
      description: 'Industrieel ventilatiesysteem met luchtbehandeling en filtering',
      systemType: 'Ventilatie',
      equipment: 'Luchtbehandelingskasten, kanaalwerk',
      capacity: '15.000 m³/h',
      certification: 'VCA, ATEX',
      hourlyRate: '€90',
      energyRating: 'A+'
    },
    {
      title: 'Smart klimaatbeheersing woning',
      location: 'Utrecht',
      budget: '€8.000 - €12.000',
      deadline: '4 weken',
      description: 'Slimme klimaatbeheersing met warmtepomp en vloerverwarming',
      systemType: 'Smart Climate',
      equipment: 'Warmtepomp, vloerverwarming, smart thermostaat',
      capacity: '12 kW',
      certification: 'F-gas, Daikin certified',
      hourlyRate: '€75',
      energyRating: 'A+++'
    },
    {
      title: 'Onderhoud HVAC-systemen ziekenhuis',
      location: 'Den Haag',
      budget: '€15.000 - €20.000',
      deadline: 'Doorlopend contract',
      description: 'Jaarlijks onderhoudscontract HVAC-systemen medische faciliteit',
      systemType: 'Onderhoud',
      equipment: 'Diverse HVAC-systemen',
      capacity: 'Variabel',
      certification: 'F-gas, VCA, BRL 6000',
      hourlyRate: '€80',
      energyRating: 'N/A'
    },
    {
      title: 'Warmtepomp installatie nieuwbouw',
      location: 'Groningen',
      budget: '€12.000 - €18.000',
      deadline: '5 weken',
      description: 'Lucht-water warmtepomp met vloerverwarming en koeling',
      systemType: 'Warmtepomp',
      equipment: 'Lucht-water warmtepomp, buffervat',
      capacity: '16 kW',
      certification: 'F-gas, Mitsubishi certified',
      hourlyRate: '€75',
      energyRating: 'A+++'
    }
  ];

  const systemTypes = [
    { value: 'all', label: 'Alle systemen' },
    { value: 'heating', label: 'Verwarming' },
    { value: 'cooling', label: 'Koeling' },
    { value: 'ventilation', label: 'Ventilatie' },
    { value: 'heatpump', label: 'Warmtepompen' },
    { value: 'smart', label: 'Smart Climate' },
    { value: 'maintenance', label: 'Onderhoud' }
  ];

  const getEnergyRatingColor = (rating) => {
    if (rating?.includes('A+++')) return 'bg-success/10 text-success border-success/20';
    if (rating?.includes('A++')) return 'bg-success/10 text-success border-success/20';
    if (rating?.includes('A+')) return 'bg-warning/10 text-warning border-warning/20';
    return 'bg-muted text-muted-foreground border-border';
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#0A0A0A]">
        {/* Hero Section */}
        <motion.section
          className="py-16 md:py-24"
          style={{ background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Icon name="Wind" size={32} className="text-blue-600" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                  HVAC Systemen - Vakgebied Browser
                </h1>
              </div>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Gespecialiseerde verwarming, ventilatie en airconditioning opdrachten met gecertificeerde technici voor residentiële, commerciële en industriële projecten.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve opdrachten', value: '67', icon: 'Briefcase' },
                { label: 'Gecertificeerde technici', value: '234', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.9', icon: 'Star' },
                { label: 'Voltooide projecten', value: '3.5k+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6" style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-4">Geavanceerde Filters</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Systeemtype</label>
                  <select 
                    value={selectedSystemType}
                    onChange={(e) => setSelectedSystemType(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {systemTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Locatie Radius</label>
                  <select 
                    value={locationRadius}
                    onChange={(e) => setLocationRadius(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="10">Binnen 10 km</option>
                    <option value="25">Binnen 25 km</option>
                    <option value="50">Binnen 50 km</option>
                    <option value="100">Binnen 100 km</option>
                    <option value="all">Alle locaties</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Certificering</label>
                  <select className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="all">Alle certificeringen</option>
                    <option value="fgas">F-gas</option>
                    <option value="epa">EPA</option>
                    <option value="vca">VCA</option>
                    <option value="manufacturer">Fabrikant Certificering</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-heading font-semibold text-foreground">
                  Beschikbare HVAC Opdrachten
                </h2>
                <span className="text-sm text-muted-foreground">{hvacJobs?.length} opdrachten gevonden</span>
              </div>
              <div className="grid gap-6">
                {hvacJobs?.map((job, index) => (
                  <motion.div
                    key={index}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6 transition-all"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon name="Wind" size={24} className="text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-foreground mb-2">{job?.title}</h3>
                            <p className="text-muted-foreground mb-3">{job?.description}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="MapPin" size={16} />
                            <span>{job?.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="Calendar" size={16} />
                            <span>{job?.deadline}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="Settings" size={16} />
                            <span>{job?.systemType}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="Gauge" size={16} />
                            <span>{job?.capacity}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="Award" size={16} />
                            <span>{job?.certification}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="Package" size={16} />
                            <span className="truncate">{job?.equipment}</span>
                          </div>
                          <div className="flex items-center gap-2 text-primary font-medium">
                            <Icon name="DollarSign" size={16} />
                            <span>{job?.budget}</span>
                          </div>
                          <div className="flex items-center gap-2 text-primary font-medium">
                            <Icon name="Clock" size={16} />
                            <span>{job?.hourlyRate}/uur</span>
                          </div>
                        </div>
                        <div className="mt-3">
                          {job?.energyRating !== 'N/A' && (
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getEnergyRatingColor(job?.energyRating)}`}>
                              <Icon name="Zap" size={12} />
                              Energie: {job?.energyRating}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 lg:w-48">
                        <Button className="w-full">Opdracht Accepteren</Button>
                        <Button variant="outline" className="w-full">Systeem Details</Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Technicians Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Gecertificeerde HVAC Specialisten</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Users" size={16} />
                  <span>{certifiedInstallers?.length} specialisten beschikbaar</span>
                </div>
              </div>

              {loadingInstallers ? (
                <div className="text-center py-8 text-muted-foreground">Laden...</div>
              ) : certifiedInstallers?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Geen specialisten beschikbaar</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {certifiedInstallers?.map((installer, index) => (
                    <motion.div
                      key={installer?.id || index}
                      className="bg-white border-2 border-slate-200 rounded-xl p-6"
                      style={{
                        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{installer?.name}</h3>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center gap-1">
                              <Icon name="Star" size={14} className="text-warning fill-warning" />
                              <span className="text-sm font-medium text-foreground">{installer?.rating}</span>
                              <span className="text-xs text-muted-foreground">({installer?.reviews} reviews)</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="MapPin" size={14} />
                            <span>{installer?.location}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Specialisatie</div>
                        <div className="text-sm text-primary font-medium">{installer?.specialization}</div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Certificeringen</div>
                        <div className="flex flex-wrap gap-2">
                          {installer?.certifications?.map((cert, idx) => (
                            <span key={idx} className="px-2 py-1 bg-success/10 text-success text-xs rounded-full border border-success/20">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Garantie</div>
                        <div className="text-sm text-muted-foreground">{installer?.warranty}</div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Ervaring</div>
                          <div className="text-sm font-medium text-foreground">{installer?.experience}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Voltooide projecten</div>
                          <div className="text-sm font-medium text-foreground">{installer?.completed_projects}+</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Uurtarief</div>
                          <div className="text-xl font-bold text-foreground">{installer?.hourly_rate}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground mb-1">Beschikbaarheid</div>
                            <div className="text-sm font-medium text-success">{installer?.availability}</div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="ml-2"
                            onClick={() => handleContactClick(installer)}
                          >
                            <Icon name="Phone" size={14} />
                            Contacteren
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Area Content Section */}
        {!loadingContent && areaContent?.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-8">Waarom kiezen voor onze HVAC specialisten?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {areaContent?.map((content, index) => (
                  <motion.div
                    key={content?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{content?.title}</h3>
                        <p className="text-sm text-muted-foreground">{content?.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest News Section */}
        {!loadingContent && news?.length > 0 && (
          <section className="py-12 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Laatste Nieuws</h2>
                <Icon name="Newspaper" size={24} className="text-primary" />
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {news?.map((newsItem, index) => (
                  <motion.div
                    key={newsItem?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(newsItem?.published_date)?.toLocaleDateString('nl-NL')}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">{newsItem?.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{newsItem?.content}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="User" size={14} />
                      <span>{newsItem?.author}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12"
            >
              <Icon name="Wind" size={48} className="text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                Klaar om te starten met uw HVAC project?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Plaats uw opdracht en ontvang offertes van gecertificeerde HVAC-technici met transparante tarieven en directe communicatie.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/opdracht-plaatsen" className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium">
                  Opdracht Plaatsen
                </Link>
                <Link to="/contractor-job-browser" className="px-8 py-3 border border-border bg-card text-foreground rounded-lg hover:bg-muted transition-colors font-medium">
                  Alle Opdrachten Bekijken
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default JobSectorBrowserHVACSystems;